import { DEFAULT_RULER_SCALE, normalizeRulerScale } from './rulerScale.js';

export const RULER_SIZE = 20; // px — width of left ruler / height of top ruler

const BG_COLOR      = '#1f2937';
const TICK_COLOR    = '#9ca3af';
const TEXT_COLOR    = '#9ca3af';
const HANDLE_COLOR  = '#3B82F6';
const FONT          = '9px sans-serif';
const TICK_MAJOR    = 10; // px tall
const TICK_MINOR    = 5;
const LABEL_EVERY   = 5; // every Nth ruler tick gets a label

/**
 * Manages two HTML <canvas> rulers (top + left) and a corner square.
 * Listens for mousedown on either ruler to begin guideline creation via drag.
 *
 * Events emitted via `onCreateGuideline`:
 *   { orientation: 'horizontal'|'vertical', rawPosition: number (content-space) }
 *
 * Call `update()` whenever stage pans or zooms.
 * Call `drawHandles(guidelines)` to paint the guideline handle markers inside the rulers.
 */
export default class RulerCanvas {
  constructor({ wrapperEl, stage, guidelineManager, scale = DEFAULT_RULER_SCALE }) {
    this.stage   = stage;
    this.manager = guidelineManager;
    this.scale = normalizeRulerScale(scale);
    // Ruler-created guidelines snap to the same interval shown on the ruler.
    this.manager.gridSize = this.scale;
    this.onCreateGuideline = null; // callback(orientation, rawPosition)

    this._hCanvas = this._createCanvas('ruler-h', 'absolute top-0 left-0 z-10 pointer-events-auto');
    this._vCanvas = this._createCanvas('ruler-v', 'absolute top-0 left-0 z-10 pointer-events-auto');
    this._corner  = this._createCorner();

    wrapperEl.appendChild(this._corner);
    wrapperEl.appendChild(this._hCanvas);
    wrapperEl.appendChild(this._vCanvas);

    this._bindEvents();
  }

  // ── Public API ─────────────────────────────────────────────────────────

  update() {
    this._resize();
    this._drawH();
    this._drawV();
    this._drawHandles();
  }

  resize() { this._resize(); this.update(); }

  setScale(value) {
    this.scale = normalizeRulerScale(value);
    this.manager.gridSize = this.scale;
    this.update();
  }

  // ── DOM creation ────────────────────────────────────────────────────────

  _createCanvas(id, cls) {
    const c = document.createElement('canvas');
    c.id = id;
    c.className = cls;
    c.style.position = 'absolute';
    return c;
  }

  _createCorner() {
    const d = document.createElement('div');
    d.id = 'ruler-corner';
    d.style.cssText = `position:absolute;top:0;left:0;width:${RULER_SIZE}px;height:${RULER_SIZE}px;background:${BG_COLOR};z-index:11;`;
    return d;
  }

  _resize() {
    const s = this.stage;
    const w = s.width();
    const h = s.height();
    // Horizontal ruler: full width, RULER_SIZE tall, positioned at top
    this._hCanvas.style.top  = '0px';
    this._hCanvas.style.left = '0px';
    this._hCanvas.width  = w;
    this._hCanvas.height = RULER_SIZE;
    // Vertical ruler: RULER_SIZE wide, full height, positioned at left
    this._vCanvas.style.top  = '0px';
    this._vCanvas.style.left = '0px';
    this._vCanvas.width  = RULER_SIZE;
    this._vCanvas.height = h;
  }

  // ── Drawing ─────────────────────────────────────────────────────────────

  _drawH() {
    const c = this._hCanvas;
    const ctx = c.getContext('2d');
    const { stageX, scale } = this._stageInfo();
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, c.width, c.height);

    ctx.strokeStyle = TICK_COLOR;
    ctx.fillStyle   = TEXT_COLOR;
    ctx.font        = FONT;
    ctx.textAlign   = 'center';
    ctx.textBaseline = 'top';
    ctx.lineWidth   = 1;

    const screenGrid = this.scale * scale;
    if (screenGrid < 2) return;

    const contentStart = -stageX / scale;
    const firstGrid    = Math.floor(contentStart / this.scale) * this.scale;

    for (let g = firstGrid; ; g += this.scale) {
      const sx = (g - contentStart) * scale;
      if (sx > c.width) break;
      if (sx < RULER_SIZE) continue;

      const isMajor = (g / this.scale) % LABEL_EVERY === 0;
      const tickH   = isMajor ? TICK_MAJOR : TICK_MINOR;

      ctx.beginPath();
      ctx.moveTo(sx, RULER_SIZE - tickH);
      ctx.lineTo(sx, RULER_SIZE);
      ctx.stroke();

      if (isMajor && screenGrid > 6) {
        ctx.fillText(String(g), sx, 1);
      }
    }
  }

  _drawV() {
    const c = this._vCanvas;
    const ctx = c.getContext('2d');
    const { stageY, scale } = this._stageInfo();
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, c.width, c.height);

    ctx.strokeStyle = TICK_COLOR;
    ctx.fillStyle   = TEXT_COLOR;
    ctx.font        = FONT;
    ctx.textAlign   = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth   = 1;

    const screenGrid = this.scale * scale;
    if (screenGrid < 2) return;

    const contentStart = -stageY / scale;
    const firstGrid    = Math.floor(contentStart / this.scale) * this.scale;

    for (let g = firstGrid; ; g += this.scale) {
      const sy = (g - contentStart) * scale;
      if (sy > c.height) break;
      if (sy < RULER_SIZE) continue;

      const isMajor = (g / this.scale) % LABEL_EVERY === 0;
      const tickW   = isMajor ? TICK_MAJOR : TICK_MINOR;

      ctx.beginPath();
      ctx.moveTo(RULER_SIZE - tickW, sy);
      ctx.lineTo(RULER_SIZE, sy);
      ctx.stroke();

      if (isMajor && screenGrid > 6) {
        ctx.save();
        ctx.translate(RULER_SIZE / 2, sy);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(String(g), 0, 0);
        ctx.restore();
      }
    }
  }

  _drawHandles() {
    const guides = this.manager.getGuidelines();
    const { stageX, stageY, scale } = this._stageInfo();

    // Horizontal ruler handles (vertical guidelines)
    {
      const ctx = this._hCanvas.getContext('2d');
      guides.filter(g => g.orientation === 'vertical').forEach(g => {
        const sx = g.position * scale + stageX;
        if (sx < RULER_SIZE || sx > this._hCanvas.width) return;
        ctx.fillStyle = g.color;
        ctx.beginPath();
        ctx.moveTo(sx - 5, 0);
        ctx.lineTo(sx + 5, 0);
        ctx.lineTo(sx + 5, RULER_SIZE - 5);
        ctx.lineTo(sx,     RULER_SIZE);
        ctx.lineTo(sx - 5, RULER_SIZE - 5);
        ctx.closePath();
        ctx.fill();
      });
    }

    // Vertical ruler handles (horizontal guidelines)
    {
      const ctx = this._vCanvas.getContext('2d');
      guides.filter(g => g.orientation === 'horizontal').forEach(g => {
        const sy = g.position * scale + stageY;
        if (sy < RULER_SIZE || sy > this._vCanvas.height) return;
        ctx.fillStyle = g.color;
        ctx.beginPath();
        ctx.moveTo(0,             sy - 5);
        ctx.lineTo(0,             sy + 5);
        ctx.lineTo(RULER_SIZE - 5, sy + 5);
        ctx.lineTo(RULER_SIZE,    sy);
        ctx.lineTo(RULER_SIZE - 5, sy - 5);
        ctx.closePath();
        ctx.fill();
      });
    }
  }

  // ── Event binding ────────────────────────────────────────────────────────

  _bindEvents() {
    this._hCanvas.addEventListener('mousedown', (e) => this._onRulerMouseDown(e, 'vertical'));
    this._vCanvas.addEventListener('mousedown', (e) => this._onRulerMouseDown(e, 'horizontal'));
  }

  _onRulerMouseDown(e, orientation) {
    e.preventDefault();
    const { stageX, stageY, scale } = this._stageInfo();
    const rect = this._hCanvas.getBoundingClientRect();

    const rawPos = orientation === 'vertical'
      ? (e.clientX - rect.left - stageX) / scale
      : (e.clientY - rect.top  - stageY) / scale;

    // Click on an existing handle → delete that guideline
    const hit = this._hitTestHandle(orientation, rawPos);
    if (hit) {
      this.manager.removeGuideline(hit.id);
      this.update();
      return;
    }

    // Click on empty ruler area → create guideline at that position
    this.manager.addGuideline(orientation, rawPos);
    this.update();
  }

  _hitTestHandle(orientation, contentPos) {
    const HIT = 8 / this._stageInfo().scale; // 8 screen px in content units
    return this.manager.getGuidelines().find(
      g => g.orientation === orientation && Math.abs(g.position - contentPos) < HIT
    );
  }

  _stageInfo() {
    const s = this.stage;
    return {
      stageX:   s.x(),
      stageY:   s.y(),
      scale:    s.scaleX(),
    };
  }
}
